// IMPORTING Injectable FROM ANGULAR CORE.
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from './auth.service';
import { HelperService } from './helper.service';

// INJECTING TO THE APP PROVIDER SO THAT WE CAN USE IT THROUGHOUT THE APPLICATION.
@Injectable
({
	providedIn: 'root'
})

// DECLARING THE AuthGuard CLASS WITH EXPORT SO THAT WE CAN USE THIS CLASS IN OTHER FILES.
export class AuthGuard implements CanActivate
{
	// CLASS CONSTRUCTOR, THIS WILL BE THE FIRST FUNCTION TO BE EXECUTED WHEN THIS CLASS LOADS.
	// HERE WE WILL TELL ANGULAR TO INJECT A DEPENDENCY BY SPECIFYING A CONSTRUCTOR
	// PARAMETER WITH THE DEPENDENCY TYPE.
	constructor (
		private authService: AuthService,
		private router: Router,
		private helperService: HelperService
	)
	{
	}

	// THIS FUNCTION IS THE ROUTE ACTIVATOR USED IN app.routing.module. IT WILL RETURN TRUE IF USER
	// IS LOGGED IN, ELSE, REDIRECT THE USER TO LOGIN PAGE.
	async canActivate (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean>
	{
		const url: string = state.url;
		const isLoggedIn = await this.checkLogin (url);
		return (isLoggedIn && !this.helperService.isFormModified);
	}

	// THIS FUNCTION IS USED TO CHECK IF THE USER IS LOGGED IN OR NOT.
	// IF LOGGED IN, RETURN TRUE, ELSE, ROUTE THE USER TO LOGIN PAGE.
	private async checkLogin (url: string)
	{
		// IF THE USER IS LOGGED IN.
		if (this.authService.isLoggedIn ())
		{
			// AN ACCOUNT THAT HAS NEVER CHOSEN A PASSWORD GETS NOWHERE ELSE. THE SIGN-IN REDIRECT ALONE
			// WAS NOT A FORCE: THE MEMBER COULD TYPE /dashboard, OR RELOAD, AND CARRY ON.
			if (this.authService.mustSetPassword () && !url.startsWith ('/reset-password'))
			{
				this.router.navigate (['/reset-password/set-password']);
				return false;
			}

			// BLOCK CONSUMER ROLE FROM ACCESSING ADMIN PORTAL.
			const userRole = this.helperService.userInfo?.role_id;
			if (userRole === 'CONSUMER')
			{
				this.helperService.toastrService.error('You are not authorized');
				this.authService.logout('UNAUTHORIZED_ROLE');
				return false;
			}

			let isAuthorized = this.authService.isAuthorized (url);

			// IF USER IS NOT AUTHORIZED TO ACCESS THIS PAGE THEN REDIRECT THEm TO UNAUTHORIZED PAGE.
			if (!isAuthorized)
			{
				this.router.navigate (['/unauthorized']);
				return false;
			}
			return true;
		}
		else // IF THE USER IS NOT LOGGED IN.
		{
			this.authService.logout('TOKEN_EXPIRED');
			return false;
		}
	}
}
